import React, { useEffect } from 'react';
import styled from 'styled-components';
import Card from '../components/UI/Card';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Building2, BadgeCheck, BadgeX, Shield, Mail, MapPin, Users, FileText } from 'lucide-react';

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderCard = styled(Card)`
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  min-width: 0;
`;

const Avatar = styled.div<{ theme: any }>`
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary} 0%, #973031 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 800;
  font-size: 1.15rem;
  flex-shrink: 0;
`;

const TitleText = styled.div`
  min-width: 0;
`;

const FirmName = styled.h1<{ theme: any }>`
  margin: 0 0 0.25rem 0;
  color: ${p => p.theme.colors.text};
  font-size: 1.4rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubTitle = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 600;
  font-size: 0.9rem;
  flex-wrap: wrap;
`;

const Code = styled.span<{ theme: any }>`
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  color: ${p => p.theme.colors.primary};
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  font-weight: 800;
  letter-spacing: 0.4px;
`;

const Tags = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`;

const Tag = styled.span<{ theme: any; $tone: 'ok' | 'warn' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 800;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => {
    if (p.$tone === 'ok') return p.theme.colors.success + '15';
    if (p.$tone === 'warn') return p.theme.colors.error + '12';
    return p.theme.colors.surface;
  }};
  color: ${p => {
    if (p.$tone === 'ok') return p.theme.colors.success;
    if (p.$tone === 'warn') return p.theme.colors.error;
    return p.theme.colors.textSecondary;
  }};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SectionCard = styled(Card)`
  padding: 1.25rem;
`;

const FullWidthSectionCard = styled(SectionCard)`
  grid-column: 1 / -1;
`;

const SectionTitle = styled.h2<{ theme: any }>`
  margin: 0 0 1rem 0;
  color: ${p => p.theme.colors.text};
  font-size: 1.05rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Row = styled.div<{ theme: any }>`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border}40;
`;

const Label = styled.div<{ theme: any }>`
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 700;
  font-size: 0.85rem;
`;

const Value = styled.div<{ theme: any }>`
  color: ${p => p.theme.colors.text};
  font-weight: 700;
  font-size: 0.9rem;
  text-align: right;
  max-width: 62%;
  overflow-wrap: anywhere;
`;

const PartnersTable = styled.table<{ theme: any }>`
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid ${p => p.theme.colors.border};

  th, td {
    padding: 0.75rem;
    text-align: left;
    font-size: 0.9rem;
    border-bottom: 1px solid ${p => p.theme.colors.border};
  }

  th {
    color: ${p => p.theme.colors.textSecondary};
    background: ${p => p.theme.colors.background};
    font-weight: 800;
  }

  td {
    color: ${p => p.theme.colors.text};
    font-weight: 600;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const DocList = styled.ul<{ theme: any }>`
  margin: 0;
  padding: 0 0 0 1.15rem;
  color: ${p => p.theme.colors.text};

  li {
    margin: 0.25rem 0;
  }
`;

const DocLink = styled.a<{ theme: any }>`
  color: ${p => p.theme.colors.primary};
  text-decoration: none;
  font-weight: 700;

  &:hover {
    text-decoration: underline;
  }
`;

function initialsFromFirmName(firmName?: string | null) {
  const base = (firmName || '').trim();
  if (!base) return 'TC';
  return base
    .split(/\s+/)
    .map(s => s[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function fmtDate(s?: string | null) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
}

function toUrlList(v: unknown): string[] {
  if (!v) return [];
  if (Array.isArray(v)) return v.filter(Boolean).map(String);
  if (typeof v === 'string') return v.trim() ? [v.trim()] : [];
  return [];
}

const ProfilePage: React.FC = () => {
  const { theme } = useTheme();
  const { consultant, isLoading, isAuthenticated, refreshConsultant } = useAuth();
  usePageTitle('Profile');

  useEffect(() => {
    if (!isAuthenticated) return;
    if (consultant) return;
    if (isLoading) return;
    void refreshConsultant();
  }, [consultant, isAuthenticated, isLoading, refreshConsultant]);

  const status = (consultant?.status as string | undefined) || '—';
  const statusOk = String(status).toUpperCase() === 'APPROVED';

  return (
    <Container>
      <HeaderCard>
        <HeaderRow>
          <TitleGroup>
            <Avatar theme={theme}>{initialsFromFirmName(consultant?.firmName)}</Avatar>
            <TitleText>
              <FirmName theme={theme}>{consultant?.firmName || consultant?.name || 'Consultant Profile'}</FirmName>
              <SubTitle theme={theme}>
                <Shield size={16} />
                <Code theme={theme}>{consultant?.taxId || '—'}</Code>
              </SubTitle>
            </TitleText>
          </TitleGroup>

          <Tags>
            <Tag theme={theme} $tone="neutral">
              <Building2 size={14} />
              {consultant?.practice || '—'}
            </Tag>
            <Tag theme={theme} $tone={statusOk ? 'ok' : 'warn'}>
              {statusOk ? <BadgeCheck size={14} /> : <BadgeX size={14} />}
              {status}
            </Tag>
          </Tags>
        </HeaderRow>
      </HeaderCard>

      <Grid>
        <SectionCard>
          <SectionTitle theme={theme}><Mail size={18} /> Contact</SectionTitle>
          <Rows>
            <Row theme={theme}>
              <Label theme={theme}>Name</Label>
              <Value theme={theme}>{consultant?.name || '—'}</Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Email</Label>
              <Value theme={theme}>{consultant?.email || '—'}</Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Phone</Label>
              <Value theme={theme}>{consultant?.phone || '—'}</Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Business Address</Label>
              <Value theme={theme}>{consultant?.businessAddr || '—'}</Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Tax ID</Label>
              <Value theme={theme}>{consultant?.taxId || '—'}</Value>
            </Row>
          </Rows>
        </SectionCard>

        <SectionCard>
          <SectionTitle theme={theme}><BadgeCheck size={18} /> Professional</SectionTitle>
          <Rows>
            <Row theme={theme}>
              <Label theme={theme}>Professional Body</Label>
              <Value theme={theme}>{consultant?.professionalBody || '—'}</Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Membership No</Label>
              <Value theme={theme}>{consultant?.membershipNo || '—'}</Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>License No</Label>
              <Value theme={theme}>{consultant?.professionalLicenseNo || '—'}</Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Years in Practice</Label>
              <Value theme={theme}>{typeof consultant?.yearsInPractice === 'number' ? consultant.yearsInPractice : '—'}</Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>CAC Number</Label>
              <Value theme={theme}>{consultant?.cacNumber || '—'}</Value>
            </Row>
          </Rows>
        </SectionCard>

        <FullWidthSectionCard>
          <SectionTitle theme={theme}><FileText size={18} /> Documents</SectionTitle>
          <Rows>
            <Row theme={theme}>
              <Label theme={theme}>CAC Certificate URLs</Label>
              <Value theme={theme}>
                {toUrlList(consultant?.cacCertUrls).length > 0 ? (
                  <DocList theme={theme}>
                    {toUrlList(consultant?.cacCertUrls).map((u) => (
                      <li key={u}>
                        <DocLink theme={theme} href={u} target="_blank" rel="noreferrer">{u}</DocLink>
                      </li>
                    ))}
                  </DocList>
                ) : '—'}
              </Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Professional License URLs</Label>
              <Value theme={theme}>
                {toUrlList(consultant?.profLicenseCertUrls).length > 0 ? (
                  <DocList theme={theme}>
                    {toUrlList(consultant?.profLicenseCertUrls).map((u) => (
                      <li key={u}>
                        <DocLink theme={theme} href={u} target="_blank" rel="noreferrer">{u}</DocLink>
                      </li>
                    ))}
                  </DocList>
                ) : '—'}
              </Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Tax Clearance URLs</Label>
              <Value theme={theme}>
                {toUrlList(consultant?.taxClearanceCertUrls).length > 0 ? (
                  <DocList theme={theme}>
                    {toUrlList(consultant?.taxClearanceCertUrls).map((u) => (
                      <li key={u}>
                        <DocLink theme={theme} href={u} target="_blank" rel="noreferrer">{u}</DocLink>
                      </li>
                    ))}
                  </DocList>
                ) : '—'}
              </Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Membership Certificate URLs</Label>
              <Value theme={theme}>
                {toUrlList(consultant?.membershipCertUrls).length > 0 ? (
                  <DocList theme={theme}>
                    {toUrlList(consultant?.membershipCertUrls).map((u) => (
                      <li key={u}>
                        <DocLink theme={theme} href={u} target="_blank" rel="noreferrer">{u}</DocLink>
                      </li>
                    ))}
                  </DocList>
                ) : '—'}
              </Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Valid ID URLs</Label>
              <Value theme={theme}>
                {toUrlList(consultant?.validIdUrls).length > 0 ? (
                  <DocList theme={theme}>
                    {toUrlList(consultant?.validIdUrls).map((u) => (
                      <li key={u}>
                        <DocLink theme={theme} href={u} target="_blank" rel="noreferrer">{u}</DocLink>
                      </li>
                    ))}
                  </DocList>
                ) : '—'}
              </Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Passport Photo</Label>
              <Value theme={theme}>
                {toUrlList(consultant?.passportPhoto).length > 0 ? (
                  <DocList theme={theme}>
                    {toUrlList(consultant?.passportPhoto).map((u) => (
                      <li key={u}>
                        <DocLink theme={theme} href={u} target="_blank" rel="noreferrer">{u}</DocLink>
                      </li>
                    ))}
                  </DocList>
                ) : '—'}
              </Value>
            </Row>
          </Rows>
        </FullWidthSectionCard>

        <FullWidthSectionCard>
          <SectionTitle theme={theme}><MapPin size={18} /> Audit</SectionTitle>
          <Rows>
            <Row theme={theme}>
              <Label theme={theme}>Consultant ID</Label>
              <Value theme={theme}>{consultant?.id || '—'}</Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>TC Identifier</Label>
              <Value theme={theme}>{consultant?.tcIdentifier || '—'}</Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Documents Prefix</Label>
              <Value theme={theme}>{consultant?.documentsPrefix || '—'}</Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Created At</Label>
              <Value theme={theme}>{fmtDate(consultant?.createdAt)}</Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Updated At</Label>
              <Value theme={theme}>{fmtDate(consultant?.updatedAt)}</Value>
            </Row>
            <Row theme={theme}>
              <Label theme={theme}>Status Reason</Label>
              <Value theme={theme}>{consultant?.statusReason || '—'}</Value>
            </Row>
          </Rows>
        </FullWidthSectionCard>

        <FullWidthSectionCard>
          <SectionTitle theme={theme}><Users size={18} /> Partners</SectionTitle>
          {Array.isArray(consultant?.partners) && consultant.partners.length > 0 ? (
            <PartnersTable theme={theme}>
              <thead>
                <tr>
                  <th>Professional Body</th>
                  <th>Membership No</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Years</th>
                </tr>
              </thead>
              <tbody>
                {consultant.partners.map((p: any) => (
                  <tr key={p?.id || `${p?.email || ''}-${p?.membershipNo || ''}`}>
                    <td>{p?.professionalBody || '—'}</td>
                    <td>{p?.membershipNo || '—'}</td>
                    <td>{p?.email || '—'}</td>
                    <td>{p?.phone || '—'}</td>
                    <td>{typeof p?.yearsInPractice === 'number' ? p.yearsInPractice : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </PartnersTable>
          ) : (
            <div style={{ color: theme.colors.textSecondary, fontWeight: 600 }}>
              No partners recorded.
            </div>
          )}
        </FullWidthSectionCard>
      </Grid>
    </Container>
  );
};

export default ProfilePage;

